import React, { useState } from 'react';
import { Calendar, Clock, Building2, Video, Plus, Search, Filter, ChevronDown, List, CalendarDays, Eye, Edit, Trash2, AlertCircle, CheckCircle, Loader, X, ChevronLeft, ChevronRight, Users, TrendingUp, FileText, Briefcase, Target, MessageSquare, Brain, Zap, MapPin, Phone, Link as LinkIcon } from 'lucide-react';

const ScheduleModal = ({ 
  setShowScheduleModal,
  setNewInterview,
  newInterview,
  applications,
  suggestedRoles,
  handleScheduleInterview
}) => {
  const [showCustomRole, setShowCustomRole] = useState(false);
  const [errors, setErrors] = useState({});

  // Get minimum date (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Validate form before submission
  const validateForm = () => {
    const newErrors = {};

    if (newInterview.type === 'real') {
      if (!newInterview.applicationId) {
        newErrors.applicationId = 'Please select an application';
      }
      if (!newInterview.interview_format) {
        newErrors.interview_format = 'Please select interview format';
      }
      if (newInterview.interview_format === 'virtual' && !newInterview.meeting_link) {
        newErrors.meeting_link = 'Please enter the meeting link';
      }
      if (newInterview.interview_format === 'in-person' && !newInterview.location) {
        newErrors.location = 'Please enter the location';
      }
    }

    if (newInterview.type === 'mock') {
      if (!newInterview.role && !newInterview.customRole) {
        newErrors.role = 'Please select or enter a role';
      }
    }

    if (!newInterview.date) {
      newErrors.date = 'Please select a date';
    } else {
      const selectedDate = new Date(newInterview.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate < today) {
        newErrors.date = 'Cannot schedule interviews in the past';
      }
    }

    if (!newInterview.time) {
      newErrors.time = 'Please select a time';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      handleScheduleInterview();
    }
  };

  const handleInputChange = (field, value) => {
    setNewInterview({ ...newInterview, [field]: value });
    // Clear error for this field
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const handleTypeChange = (type) => {
    setNewInterview({
      type,
      applicationId: '',
      customRole: '',
      role: '',
      date: '',
      time: '',
      interview_format: '',
      meeting_link: '',
      location: '',
      notes: ''
    });
    setShowCustomRole(false);
    setErrors({});
  };

  const handleRoleChange = (value) => {
    if (value === 'custom') {
      setShowCustomRole(true);
      handleInputChange('role', '');
    } else {
      setShowCustomRole(false);
      handleInputChange('role', value);
      handleInputChange('customRole', '');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-md">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between z-10">
          <h2 className="text-lg font-bold text-gray-900">Schedule Interview</h2>
          <button
            onClick={() => setShowScheduleModal(false)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Interview Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Interview Type</label>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => handleTypeChange('real')}
                className={`p-4 border-2 rounded-lg transition-all ${
                  newInterview.type === 'real'
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <Briefcase className={`w-8 h-8 mx-auto mb-2 ${
                  newInterview.type === 'real' ? 'text-blue-600' : 'text-gray-400'
                }`} />
                <div className="font-semibold">Real Interview</div>
                <div className="text-sm text-gray-600 mt-1">Link to an application</div>
              </button>

              <button
                onClick={() => handleTypeChange('mock')}
                className={`p-4 border-2 rounded-lg transition-all ${
                  newInterview.type === 'mock'
                    ? 'border-purple-600 bg-purple-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <Brain className={`w-8 h-8 mx-auto mb-2 ${
                  newInterview.type === 'mock' ? 'text-purple-600' : 'text-gray-400'
                }`} />
                <div className="font-semibold">AI Mock Interview</div>
                <div className="text-sm text-gray-600 mt-1">Practice with AI</div>
              </button>
            </div>
          </div>

          {/* Real Interview Fields */}
          {newInterview.type === 'real' && (
            <>
              {/* Application Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Application *
                </label>
                <select
                  value={newInterview.applicationId}
                  onChange={(e) => handleInputChange('applicationId', e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.applicationId ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Choose an application...</option>
                  {applications.map(app => (
                    <option key={app.id} value={app.id}>
                      {app.jobTitle} - {app.company}
                    </option>
                  ))}
                </select>
                {errors.applicationId && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.applicationId}
                  </p>
                )}
              </div>

              {/* Interview Format */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Interview Format *
                </label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => handleInputChange('interview_format', 'virtual')}
                    className={`p-3 border-2 rounded-lg transition-all ${
                      newInterview.interview_format === 'virtual'
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <Video className={`w-6 h-6 mx-auto mb-1 ${
                      newInterview.interview_format === 'virtual' ? 'text-blue-600' : 'text-gray-400'
                    }`} />
                    <p className="text-sm font-medium">Virtual</p>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleInputChange('interview_format', 'in-person')}
                    className={`p-3 border-2 rounded-lg transition-all ${
                      newInterview.interview_format === 'in-person'
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <MapPin className={`w-6 h-6 mx-auto mb-1 ${
                      newInterview.interview_format === 'in-person' ? 'text-blue-600' : 'text-gray-400'
                    }`} />
                    <p className="text-sm font-medium">In-Person</p>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleInputChange('interview_format', 'phone')}
                    className={`p-3 border-2 rounded-lg transition-all ${
                      newInterview.interview_format === 'phone'
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <Phone className={`w-6 h-6 mx-auto mb-1 ${
                      newInterview.interview_format === 'phone' ? 'text-blue-600' : 'text-gray-400'
                    }`} />
                    <p className="text-sm font-medium">Phone</p>
                  </button>
                </div>
                {errors.interview_format && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.interview_format}
                  </p>
                )}
              </div>

              {/* Meeting Link (Virtual) */}
              {newInterview.interview_format === 'virtual' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Meeting Link *
                  </label>
                  <div className="relative">
                    <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="url"
                      value={newInterview.meeting_link}
                      onChange={(e) => handleInputChange('meeting_link', e.target.value)}
                      placeholder="https://zoom.us/j/123456789"
                      className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.meeting_link ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  {errors.meeting_link && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.meeting_link}
                    </p>
                  )}
                </div>
              )}

              {/* Location (In-Person) */}
              {newInterview.interview_format === 'in-person' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location *
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={newInterview.location}
                      onChange={(e) => handleInputChange('location', e.target.value)}
                      placeholder="123 Main St, City, State"
                      className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.location ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  {errors.location && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.location}
                    </p>
                  )}
                </div>
              )}
            </>
          )}

          {/* Mock Interview Fields */}
          {newInterview.type === 'mock' && (
            <>
              {!showCustomRole ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Role *
                  </label>
                  <select
                    value={newInterview.role}
                    onChange={(e) => handleRoleChange(e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                      errors.role ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Choose a role...</option>
                    {suggestedRoles.map(role => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                    <option value="custom">✏️ Enter custom role...</option>
                  </select>
                  {errors.role && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.role}
                    </p>
                  )}
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Custom Role *
                    </label>
                    <button
                      onClick={() => {
                        setShowCustomRole(false);
                        handleInputChange('customRole', '');
                      }}
                      className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
                    >
                      <X className="w-4 h-4" />
                      Use suggested roles
                    </button>
                  </div>
                  <div className="relative">
                    <Edit className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={newInterview.customRole}
                      onChange={(e) => handleInputChange('customRole', e.target.value)}
                      placeholder="e.g., Senior Software Engineer"
                      className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                        errors.role ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  {errors.role && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.role}
                    </p>
                  )}
                </div>
              )}
            </>
          )}

          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date *</label>
              <input
                type="date"
                value={newInterview.date}
                onChange={(e) => handleInputChange('date', e.target.value)}
                min={getMinDate()}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 ${
                  newInterview.type === 'real' ? 'focus:ring-blue-500' : 'focus:ring-purple-500'
                } focus:border-transparent ${
                  errors.date ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.date && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.date}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Time *</label>
              <input
                type="time"
                value={newInterview.time}
                onChange={(e) => handleInputChange('time', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 ${
                  newInterview.type === 'real' ? 'focus:ring-blue-500' : 'focus:ring-purple-500'
                } focus:border-transparent ${
                  errors.time ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.time && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.time}
                </p>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={() => setShowScheduleModal(false)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors ${
                newInterview.type === 'real'
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-purple-600 hover:bg-purple-700'
              }`}
            >
              {newInterview.type === 'real' ? 'Continue' : 'Schedule Mock Interview'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleModal;